import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';

import { CONSOLE_VERSION } from 'src/environments/version';

/** How often to ask the BFF which version is deployed. */
const POLL_INTERVAL_MS = 60_000;

/**
 * A rolling deploy answers successive polls from mixed old/new pods, so a tab
 * can see the version flip back and forth. Force at most one document
 * navigation per tab per window; worst case the user gets one extra reload at a
 * navigation boundary once the rollout settles (design D5).
 */
const RELOAD_COOLDOWN_MS = 10 * 60_000;
const RELOAD_AT_KEY = 'console-version-reload-at';

/**
 * Deploy pickup for the frozen Angular bundle (console-automatic-upgrade).
 *
 * This app has no lazy chunks — it loads everything at bootstrap and never
 * fetches JS again, so it cannot notice a deploy on its own. It polls the
 * Svelte bundle's `/_app/version.json`, which is the version authority for the
 * *whole image* (both bundles ship together, stamped from one build-arg), and
 * compares it against the version compiled into this bundle. On a mismatch it
 * arms a reload and performs it at the next router navigation — never while the
 * user sits on a page, so in-progress form state survives (design D4).
 */
@Injectable({ providedIn: 'root' })
export class VersionUpdateService {
  /**
   * The version compiled into this bundle. Read through a field so specs can
   * override it with a realistic `sha-*` stamp; production always gets the
   * constant.
   */
  protected readonly runningVersion: string = CONSOLE_VERSION;

  private updateAvailable = false;

  constructor(private router: Router) {}

  /**
   * Begin polling and watching for navigations. A no-op for an unstamped
   * bundle (`ng serve`, mock mode, Karma): the served version would equal the
   * stamp anyway, and no timers should bleed into test runs.
   */
  start(): void {
    if (this.runningVersion === 'dev') {
      return;
    }

    window.setInterval(() => void this.checkOnce(), POLL_INTERVAL_MS);
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.onNavigationStart(event.url);
      }
    });
  }

  /**
   * One poll. Fails silent by design: a network blip, a non-200, or an HTML
   * SPA-fallback body must never surface a dialog or stop later polls. Uses the
   * global `fetch`, never `HttpClient` — the interceptor chain opens error
   * dialogs on failure and rewrites bodies, and a background poll must produce
   * no UI whatsoever (design D3).
   */
  async checkOnce(): Promise<void> {
    try {
      const response = await fetch('/_app/version.json', { cache: 'no-store' });
      if (!response.ok) {
        return;
      }

      const body: unknown = await response.json();
      const deployed = (body as { version?: unknown } | null)?.version;
      if (typeof deployed === 'string' && deployed !== this.runningVersion) {
        this.updateAvailable = true;
      }
    } catch {
      // Silent: the next interval tries again.
    }
  }

  /**
   * The user is leaving this page anyway — take them to the same URL as a
   * document navigation so the new bundle loads.
   */
  onNavigationStart(url: string): void {
    if (!this.updateAvailable || this.inCooldown()) {
      return;
    }

    this.stampReload();
    this.reload(url);
  }

  /** Seam for specs. */
  protected reload(url: string): void {
    window.location.assign(url);
  }

  private inCooldown(): boolean {
    const stamped = this.readStorage(RELOAD_AT_KEY);
    if (stamped === null) {
      return false;
    }

    const at = Number(stamped);
    return Number.isFinite(at) && Date.now() - at < RELOAD_COOLDOWN_MS;
  }

  private stampReload(): void {
    this.writeStorage(RELOAD_AT_KEY, String(Date.now()));
  }

  // sessionStorage throws rather than returning null in some privacy modes;
  // losing the cooldown is far better than breaking navigation.
  private readStorage(key: string): string | null {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private writeStorage(key: string, value: string): void {
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      // ignore
    }
  }
}
